// Botão 📡 "acompanhar este negócio no meu radar", para usar de dentro do
// Pipeline (22/09/2026).
//
// Por que tem formulário, ao contrário do 📡 do edital: o edital tem prazo na
// FONTE, então um clique basta. O negócio do pipeline não tem — medido em
// 22/09, `data_abertura` é a abertura do negócio (ou a data da sessão, quando
// veio de edital) e `data_envio`/`data_fechamento` são preenchidas depois do
// fato. Quem sabe a data de entrega da proposta é a pessoa, e o servidor
// recusa sem ela.
//
// O formulário mora aqui e não na tela do Pipeline para existir num lugar só:
// a mesma pergunta é feita em Meu Radar, pelo "+ Adicionar".
import { useState } from 'react';
import { Button, Modal, Form, DatePicker, InputNumber, Input, Select, Tooltip, Typography, App as AntApp } from 'antd';
import { RadarChartOutlined, MailOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { Se } from '../../components/Se';
import {
  radarApi, TipoProposta, ROTULO_TIPO_PROPOSTA, DIAS_ANTECEDENCIA_PADRAO,
} from './api';

const { Text } = Typography;

export function BotaoAcompanharNegocio({
  negocioId, titulo, noRadar, onMudou,
}: {
  negocioId: number;
  titulo?: string;
  /** Quando conhecido, pinta o botão. A tela do Pipeline ainda não traz esse
   *  dado, então o padrão é indefinido — o botão continua servindo para incluir
   *  e o servidor trata o reenvio como atualização (upsert por dono+negócio). */
  noRadar?: boolean;
  onMudou?: () => void;
}) {
  const { message } = AntApp.useApp();
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();

  const abrir = () => {
    form.resetFields();
    form.setFieldsValue({ dias_antecedencia: DIAS_ANTECEDENCIA_PADRAO });
    setAberto(true);
  };

  const salvar = async () => {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      await radarApi.acompanharNegocio(negocioId, {
        prazo_manual: (v.prazo_manual as Dayjs).format('YYYY-MM-DDTHH:mm:ss'),
        dias_antecedencia: v.dias_antecedencia,
        tipo_proposta: v.tipo_proposta,
        alerta_email: v.alerta_email || undefined,
        observacoes: v.observacoes ?? '',
      });
      message.success('No seu radar.');
      setAberto(false);
      onMudou?.();
    } catch (e: any) {
      message.error(e?.response?.data?.erro || e?.response?.data?.error || e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Se permissao="radar.meu.gerenciar">
      <Tooltip title={noRadar ? 'Já está no seu radar — ajuste em Meu Radar' : 'Acompanhar no meu radar'}>
        <Button
          size="small"
          icon={<RadarChartOutlined />}
          style={{ color: noRadar ? '#020D7F' : undefined }}
          onClick={(e) => { e.stopPropagation(); abrir(); }}
        />
      </Tooltip>

      <Modal
        open={aberto}
        onCancel={() => setAberto(false)}
        title={<span>📡 Acompanhar no meu radar</span>}
        width="94vw"
        style={{ maxWidth: 620, top: 40 }}
        styles={{ body: { maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' } }}
        okText="Colocar no radar"
        cancelText="Cancelar"
        confirmLoading={salvando}
        onOk={salvar}
      >
        {titulo && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            {titulo}
          </Text>
        )}
        <Form form={form} layout="vertical" size="small">
          <Form.Item
            label="Entrega da proposta" name="prazo_manual"
            rules={[{ required: true, message: 'Informe a data de entrega' }]}
            extra={<Text type="secondary" style={{ fontSize: 11 }}>
              O pipeline não guarda esse prazo — quem sabe é você.
            </Text>}
          >
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%', maxWidth: 260 }} />
          </Form.Item>

          <Form.Item
            label="O que precisa ser produzido" name="tipo_proposta"
            rules={[{ required: true, message: 'Escolha o que precisa ser feito' }]}
            extra={<Text type="secondary" style={{ fontSize: 11 }}>
              Planejamento do seu radar — não muda o documento que o sistema gera.
            </Text>}
          >
            <Select
              placeholder="Somente técnica, ou técnica e preço"
              options={(Object.keys(ROTULO_TIPO_PROPOSTA) as TipoProposta[])
                .map(k => ({ value: k, label: ROTULO_TIPO_PROPOSTA[k] }))}
            />
          </Form.Item>

          <Form.Item
            label="Avisar com antecedência de" name="dias_antecedencia"
            rules={[{ required: true, message: 'Informe a antecedência' }]}
          >
            <InputNumber min={0} max={90} precision={0} style={{ width: 160 }} addonAfter="dias" />
          </Form.Item>

          <Form.Item label="Enviar o aviso para" name="alerta_email"
            extra={<Text type="secondary" style={{ fontSize: 11 }}>Em branco, vai para o seu e-mail.</Text>}>
            <Input prefix={<MailOutlined />} placeholder="seu e-mail" />
          </Form.Item>

          <Form.Item label="Observações" name="observacoes">
            <Input.TextArea rows={3} placeholder="O que já foi conversado, o que falta…" />
          </Form.Item>
        </Form>
      </Modal>
    </Se>
  );
}
